/**
 * ArcGIS Shaded Relief = elevation color ramp × hillshade illumination.
 *
 * TiTiler has no single shaded-relief algorithm, so engines fetch the
 * hillshade tile (live URL) and a colour twin rebuilt from `gv_sr_*`
 * markers, then modulate RGB channels with hillshade illumination.
 *
 * The blend runs on `ImageData` and stays that way: background keying composes
 * on top of it, and both engines take decoded pixels, so nothing is encoded
 * between the two stages. See `tilePixelPipeline.ts`.
 */

import {
  buildShadedReliefColorTileUrl,
  isShadedReliefTileUrl,
} from '@/components/raster/applyRasterToolCard';
import {
  fetchTileImage,
  fillTilePlaceholders,
  imageDataToCanvas,
  imageDataToEngineImage,
  imageDataToPngBlob,
  runTileTask,
  throwIfAborted,
  tileImageToImageData,
} from '@/components/raster/tilePixelPipeline';
import { resolveHillshadeTileUrl } from '@/utils/hillshade';

export const SHADED_RELIEF_PROTOCOL = 'gvshadedrelief';

/**
 * Modulate elevation colour by hillshade luminance (ArcGIS-style merge).
 *
 * A raw multiply makes every colour darker, because a normally illuminated
 * flat surface is about 181 rather than 255 in GDAL hillshade. ArcGIS keeps
 * the elevation ramp near its original brightness and uses shade as relief
 * modulation around that neutral illumination. Preserve that behaviour here:
 * flat terrain is ~1.0×, shadows retain colour at 0.55×, and highlights reach
 * 1.25×. Alpha comes from the colour twin so nodata stays transparent.
 */
export function multiplyShadedRelief(
  color: ImageData,
  shade: ImageData,
): ImageData {
  const w = Math.min(color.width, shade.width);
  const h = Math.min(color.height, shade.height);
  const out = new ImageData(w, h);
  const c = color.data;
  const s = shade.data;
  const o = out.data;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const ci = (y * color.width + x) * 4;
      const si = (y * shade.width + x) * 4;
      const oi = (y * w + x) * 4;
      const lum = (s[si] + s[si + 1] + s[si + 2]) / (3 * 255);
      const neutral = 181 / 255;
      const relief = Math.max(0.55, Math.min(1.25, 0.35 + 0.7 * (lum / neutral)));
      o[oi] = Math.min(255, Math.round(c[ci] * relief));
      o[oi + 1] = Math.min(255, Math.round(c[ci + 1] * relief));
      o[oi + 2] = Math.min(255, Math.round(c[ci + 2] * relief));
      o[oi + 3] = c[ci + 3];
    }
  }
  return out;
}

/**
 * Wrap a shaded-relief TiTiler template so MapLibre fetches via our protocol.
 * Non–shaded-relief templates pass through unchanged.
 */
export function wrapShadedReliefMapLibreTemplate(template: string): string {
  if (!isShadedReliefTileUrl(template)) return template;
  return `${SHADED_RELIEF_PROTOCOL}://{z}/{x}/{y}?t=${encodeURIComponent(template)}`;
}

/**
 * Given a resolved hillshade tile URL (placeholders filled), fetch colour +
 * shade and return the blended pixels.
 */
export async function fetchShadedReliefBlendedImageData(
  hillshadeTileUrl: string,
  signal?: AbortSignal | null,
): Promise<ImageData> {
  const shadeUrl = resolveHillshadeTileUrl(hillshadeTileUrl);
  const colorUrl = buildShadedReliefColorTileUrl(shadeUrl);
  if (!colorUrl) {
    return tileImageToImageData(await fetchTileImage(shadeUrl, signal));
  }
  const [colorImg, shadeImg] = await Promise.all([
    fetchTileImage(colorUrl, signal),
    fetchTileImage(shadeUrl, signal),
  ]);
  throwIfAborted(signal);
  // Both read-backs share one scratch canvas, so they must not interleave.
  const color = tileImageToImageData(colorImg);
  const shade = tileImageToImageData(shadeImg);
  return multiplyShadedRelief(color, shade);
}

type MapLibreProtocol = {
  addProtocol: (
    name: string,
    handler: (
      request: { url: string },
      abortController?: AbortController,
    ) => Promise<{ data: ImageBitmap | ArrayBuffer }>,
  ) => void;
};

let shadedReliefProtocolRegistered = false;

/** Register the `gvshadedrelief://` protocol on MapLibre once per process. */
export function ensureShadedReliefProtocol(maplibre: MapLibreProtocol): void {
  if (shadedReliefProtocolRegistered) return;
  maplibre.addProtocol(SHADED_RELIEF_PROTOCOL, async (request, abortController) => {
    const raw = request.url;
    // MapLibre may pass `gvshadedrelief://z/x/y?t=...` or with a fake host.
    const pathMatch = /gvshadedrelief:\/\/(?:[^/]+\/)?(\d+)\/(\d+)\/(\d+)/i.exec(raw);
    const tMatch = /[?&]t=([^&]+)/.exec(raw);
    if (!pathMatch || !tMatch) throw new Error(`Bad shaded-relief URL: ${raw}`);
    const [, z, x, y] = pathMatch;
    const template = decodeURIComponent(tMatch[1]);
    const filled = fillTilePlaceholders(template, z, x, y);
    const signal = abortController?.signal ?? null;
    const data = await runTileTask(async () =>
      imageDataToEngineImage(
        await fetchShadedReliefBlendedImageData(filled, signal),
      ),
    );
    return { data };
  });
  shadedReliefProtocolRegistered = true;
}

/** Minimal view of the OpenLayers `ImageTile` a load function receives. */
export type OpenLayersImageTile = {
  getImage: () => HTMLImageElement | HTMLCanvasElement;
  setImage?: (element: HTMLCanvasElement | HTMLImageElement) => void;
  setState?: (state: number) => void;
};

/** `TileState.ERROR` — inlined to keep this module free of an `ol` import. */
const OL_TILE_STATE_ERROR = 3;

/**
 * Hand processed pixels to an OpenLayers tile.
 *
 * `setImage` takes a canvas directly and flips the tile to LOADED, which skips
 * the PNG encode / decode round-trip the `<img>.src` path would need. The
 * fallback covers tile implementations without it.
 */
export function setOpenLayersTilePixels(
  tile: OpenLayersImageTile,
  data: ImageData,
): void {
  const canvas = imageDataToCanvas(data);
  if (typeof tile.setImage === 'function') {
    tile.setImage(canvas);
    return;
  }
  const image = tile.getImage();
  if (!(image instanceof HTMLImageElement)) {
    tile.setState?.(OL_TILE_STATE_ERROR);
    return;
  }
  void imageDataToPngBlob(data).then((blob) => {
    const objectUrl = URL.createObjectURL(blob);
    const clear = () => URL.revokeObjectURL(objectUrl);
    image.addEventListener('load', clear, { once: true });
    image.addEventListener('error', clear, { once: true });
    image.src = objectUrl;
  });
}

/** Mark an OpenLayers tile failed without leaving a half-loaded image behind. */
export function failOpenLayersTile(tile: OpenLayersImageTile): void {
  const image = tile.getImage();
  if (image instanceof HTMLImageElement) image.src = '';
  tile.setState?.(OL_TILE_STATE_ERROR);
}

/**
 * OpenLayers `tileLoadFunction` that multiplies colour × hillshade for
 * shaded-relief templates. No-op when the template is plain.
 */
export function attachShadedReliefTileLoader(source: unknown, templateUrl: string): void {
  if (!isShadedReliefTileUrl(templateUrl)) return;
  const olSource = source as {
    setTileLoadFunction?: (
      fn: (tile: OpenLayersImageTile, url: string) => void,
    ) => void;
  } | null;
  if (typeof olSource?.setTileLoadFunction !== 'function') return;

  olSource.setTileLoadFunction((tile, src) => {
    void runTileTask(() => fetchShadedReliefBlendedImageData(src))
      .then((data) => setOpenLayersTilePixels(tile, data))
      .catch(() => failOpenLayersTile(tile));
  });
}
